/**
 * PreBookingForm: ログイン済み会員の事前予約フォーム（US-D2-09）。製品詳細・電子ブック閲覧・
 * お問い合わせ完了画面で使用する単一CV系UI（企業詳細はSurveyPopup経由でこのフォームへ遷移する）。
 * BR-U6-2: フォーム自体は未ログインでも表示される。確定操作時のみログイン検証。
 *
 * <p>design-mockups/product-detail-tag-proposal.html, ebook-viewing-tag-proposal.html,
 * inquiry-complete-tag-proposal.html準拠（2026-08-19デザイン更新）:
 * - WidgetPanel共通ラッパー（左下固定パネル）を採用し、未ログイン時はパネル内にログインゲートを重畳する
 *   （従来の別ポップアップ＝loginPromptOverlayUiは使用しない）。
 * - 商談形式選択・メッセージ入力・日程調整・完了表示の本体ロジックは`bookingSectionUi.ts`
 *   （SurveyPopupと共有する共通モジュール）に委譲する。
 */

import { createElement, createShadowHost, applyStylesheet } from "../core/shadowDomUtil";
import { createWidgetPanel, buildSingleCvLeadText, resolveSingleCvPanelPosition, WIDGET_PANEL_STYLES } from "../core-ui/widgetPanelUi";
import { createCalendarModal, CALENDAR_MODAL_STYLES } from "../core-ui/calendarModalUi";
import { MEETING_MODE_LABEL_STYLES } from "../core-ui/meetingModeLabelUi";
import { CALENDAR_ADD_ACTIONS_STYLES } from "../core-ui/calendarAddActionsUi";
import { LOADING_SPINNER_STYLES } from "../core-ui/loadingSpinnerUi";
import { renderBookingSection } from "./bookingSectionUi";
import { trackWidgetMinimize } from "../w3-tracking/trackPageEvent";
import type { PageEventPayload } from "../types";
import { CONSENT_TEXT_STYLES } from "../core-ui/consentTextUi";
import { LOGGED_OUT_MOSAIC_STYLES } from "../core-ui/loggedOutMosaicUi";
import {
  DESIGN_TOKEN_CSS,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  TEXT_MUTED,
  BORDER_DEFAULT,
  SURFACE_SUBTLE,
  SURFACE_SELECTED,
  BRAND_PRIMARY,
  RADIUS_DEFAULT,
} from "../core-ui/designTokens";

export const BOOKING_CARD_STYLES = `
.spica-proposal-item { position: relative; padding-left: 18px; }
.spica-proposal-item::before {
  content: ""; position: absolute; left: 0; top: 2px; bottom: 2px;
  width: 5px; border-radius: 2px; background: ${BRAND_PRIMARY};
}
.spica-proposal-card {
  background: #fff; border: 1px solid ${BORDER_DEFAULT}; border-radius: ${RADIUS_DEFAULT};
  padding: 24px 28px; position: relative;
}

.spica-company-name { font-size: 16px; font-weight: 700; color: ${TEXT_PRIMARY}; margin: 0 0 4px; display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 6px; }
.spica-content-name { font-size: 13px; color: ${TEXT_SECONDARY}; margin: 0 0 16px; }

.spica-slot-list { list-style: none; margin: 0 0 10px; padding: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.spica-slot-list li { display: flex; }
.spica-slot-btn {
  box-sizing: border-box; width: 100%; padding: 7px 6px; border: 1px solid ${BORDER_DEFAULT};
  border-radius: ${RADIUS_DEFAULT}; background: #fff; color: ${TEXT_PRIMARY};
  cursor: pointer; text-align: center; white-space: nowrap; font: inherit; font-size: 11px;
}
.spica-slot-btn:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }
.spica-slot-btn.selected { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SELECTED}; font-weight: 700; }
.spica-slot-more-btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 4px;
  border: 1px dashed #999999; background: #fff; color: ${TEXT_SECONDARY}; font-weight: 700;
}
.spica-slot-more-btn:hover { border-color: ${TEXT_PRIMARY}; color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }
.spica-slot-expanded { margin-top: 4px; }
.spica-slot-back-btn { border: none; background: none; color: ${TEXT_PRIMARY}; font-size: 12px; font-weight: 700; cursor: pointer; padding: 0 0 12px; text-decoration: underline; }

.spica-calendar-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.spica-calendar-view { display: none; }
.spica-calendar-view.is-open { display: block; margin-top: 4px; }
.spica-calendar-nav-btn { border: 1px solid ${BORDER_DEFAULT}; background: #fff; width: 26px; height: 26px; border-radius: ${RADIUS_DEFAULT}; font-size: 12px; cursor: pointer; color: ${TEXT_PRIMARY}; }
.spica-calendar-nav-btn:hover { background: ${SURFACE_SUBTLE}; }
.spica-calendar-month-label { font-size: 12px; font-weight: 700; color: ${TEXT_PRIMARY}; }
.spica-calendar-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; text-align: center; margin-bottom: 10px; }
.spica-calendar-weekday { font-size: 10px; color: #999999; padding-bottom: 3px; }
.spica-calendar-day { border: none; background: transparent; border-radius: ${RADIUS_DEFAULT}; padding: 5px 0; font-size: 11px; color: ${TEXT_PRIMARY}; cursor: pointer; }
.spica-calendar-day.has-slot { background: ${SURFACE_SUBTLE}; color: ${TEXT_PRIMARY}; font-weight: 700; }
.spica-calendar-day.has-slot:hover { background: ${SURFACE_SELECTED}; }
.spica-calendar-day.selected { background: ${TEXT_PRIMARY}; color: #fff; }
.spica-calendar-day.selected:hover { background: ${TEXT_PRIMARY}; color: #fff; }
.spica-calendar-day.disabled { color: #cccccc; cursor: default; }
.spica-calendar-day-slots { border-top: 1px solid ${BORDER_DEFAULT}; padding-top: 8px; margin-bottom: 10px; }
.spica-calendar-day-slots-label { font-size: 11px; color: ${TEXT_SECONDARY}; margin: 0 0 6px; }
.spica-calendar-slot-list { list-style: none; margin: 0; padding: 0 0 4px; display: flex; flex-wrap: nowrap; gap: 6px; overflow-x: auto; }
.spica-calendar-slot-list li { flex: 0 0 auto; }
.spica-calendar-slot-btn { box-sizing: border-box; flex: 0 0 auto; height: 32px; padding: 6px 10px; border: 1px solid ${BORDER_DEFAULT}; border-radius: ${RADIUS_DEFAULT}; background: #fff; text-align: center; cursor: pointer; white-space: nowrap; font: inherit; font-size: 11px; color: ${TEXT_PRIMARY}; }
.spica-calendar-slot-btn:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }
.spica-calendar-slot-btn.selected { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SELECTED}; font-weight: 700; }

.spica-confirm-button {
  width: 100%; margin-top: 8px; padding: 12px; border: none; border-radius: ${RADIUS_DEFAULT};
  background: ${BRAND_PRIMARY}; color: #fff; cursor: pointer; font-size: 13.5px; font-weight: 700; font-family: inherit;
}
.spica-confirm-button:disabled { background: ${BORDER_DEFAULT}; color: #999999; cursor: not-allowed; }
.spica-request-note { font-size: 12px; color: ${TEXT_SECONDARY}; margin-bottom: 10px; }
.spica-request-candidate-label { font-size: 11px; font-weight: 700; color: ${TEXT_PRIMARY}; margin: 10px 0 4px; }
.spica-request-candidate-label:first-of-type { margin-top: 0; }
.spica-request-candidate-value { font-size: 12px; color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; border: 1px solid ${BORDER_DEFAULT}; border-radius: ${RADIUS_DEFAULT}; padding: 6px 8px; margin-bottom: 6px; cursor: pointer; }
.spica-request-candidate-value.empty { color: #999999; }
.spica-request-candidate-value.active { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SELECTED}; font-weight: 700; }
.spica-request-hint { font-size: 11px; color: ${TEXT_SECONDARY}; margin: 0 0 8px; }
.spica-proposal-noslot { font-size: 13px; color: ${TEXT_PRIMARY}; margin: 0; font-weight: 700; }

.spica-meeting-type-group { display: flex; gap: 8px; margin: 12px 0; }
.spica-meeting-type-option {
  flex: 1; border: 1px solid ${BORDER_DEFAULT}; border-radius: ${RADIUS_DEFAULT}; padding: 9px 8px;
  background: #fff; cursor: pointer; text-align: center; color: ${TEXT_PRIMARY}; font: inherit; font-size: 12px; font-weight: 500;
}
.spica-meeting-type-option:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }
.spica-meeting-type-option.selected { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SELECTED}; font-weight: 700; }
.spica-onsite-note {
  font-size: 11px; color: ${TEXT_SECONDARY}; background: ${SURFACE_SUBTLE}; border: 1px solid ${BORDER_DEFAULT};
  border-radius: ${RADIUS_DEFAULT}; padding: 8px 10px; margin-bottom: 12px; line-height: 1.6;
}

.spica-input-label { font-size: 11px; font-weight: 700; color: ${TEXT_PRIMARY}; margin: 0 0 4px; }
.spica-input-field {
  width: 100%; box-sizing: border-box; border: 1px solid ${BORDER_DEFAULT}; border-radius: ${RADIUS_DEFAULT};
  padding: 8px 10px; font-size: 12px; color: ${TEXT_PRIMARY}; margin-bottom: 12px; resize: vertical;
  font-family: inherit;
}
.spica-input-field::placeholder { color: #999999; }

.spica-request-toggle-btn {
  display: block; width: 100%; text-align: left; border: none; background: none;
  color: ${TEXT_PRIMARY}; cursor: pointer; padding: 6px 0 2px; font: inherit; font-size: 11px; font-weight: 700;
}
.spica-request-toggle-btn::before { content: "▸ "; }

/* 2026-09-30 追加: 日時候補確定後に通常リクエスト（空き枠選択）へ戻す控えめなテキストリンク。
   極力画面変更を抑えるため、アンダーラインなし・小さめ（10px）・淡色（TEXT_MUTED）とする。 */
.spica-request-back-link {
  display: inline-block; border: none; background: none; color: ${TEXT_MUTED};
  cursor: pointer; padding: 0; margin: 0 0 8px; font: inherit; font-size: 10px; line-height: 1.4; text-decoration: none;
}
.spica-request-back-link::before { content: "< "; }
.spica-request-back-link:hover { color: ${TEXT_SECONDARY}; }

.spica-proposal-candidate-value-static {
  font-size: 12px; color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; border: 1px solid ${BORDER_DEFAULT};
  border-radius: ${RADIUS_DEFAULT}; padding: 6px 8px; margin-bottom: 6px; cursor: pointer;
}
.spica-proposal-candidate-value-static:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SELECTED}; }

.spica-request-confirm-btn {
  width: 100%; margin-top: 16px; padding: 8px 14px; border: 1px solid ${BORDER_DEFAULT};
  border-radius: ${RADIUS_DEFAULT}; background: #fff; color: ${TEXT_PRIMARY};
  font-size: 12px; font-weight: 700; cursor: pointer; font-family: inherit;
}
.spica-request-confirm-btn:hover { border-color: ${TEXT_PRIMARY}; background: ${SURFACE_SUBTLE}; }

.spica-onsite-request-note {
  margin: 6px 0 0; color: ${TEXT_SECONDARY}; font-size: 11px; line-height: 1.5;
}
`;

const PRE_BOOKING_FORM_STYLES = `
:host { all: initial; ${DESIGN_TOKEN_CSS} }
${WIDGET_PANEL_STYLES}
${CALENDAR_MODAL_STYLES}
${MEETING_MODE_LABEL_STYLES}
${CALENDAR_ADD_ACTIONS_STYLES}
${LOADING_SPINNER_STYLES}
${BOOKING_CARD_STYLES}
${CONSENT_TEXT_STYLES}
${LOGGED_OUT_MOSAIC_STYLES}
`;

export async function renderPreBookingForm(
  memberGroupId: string,
  screenType: string,
  objectType: string,
  objectRootId: string,
  meetingModePolicy: "ONLINE_ONLY" | "ON_SITE_ONLY" | "HYBRID" | null | undefined,
  prFlg?: boolean | null,
  companyName?: string | null,
  contentName?: string | null,
  isMainCv?: boolean,
  companyRootId?: number | null
): Promise<void> {
  const { host, shadowRoot } = createShadowHost(`spica-prebooking-form-${objectRootId}`);
  applyStylesheet(shadowRoot, PRE_BOOKING_FORM_STYLES);

  // 効果分析ログ（BR-AN-14）: 縮小トグルで畳んだ時（離脱意思）に WIDGET_MINIMIZE を発火するための文脈。
  const analyticsCtx: PageEventPayload = { screenType, objectType, objectRootId, companyRootId, prFlg, isMainCv };

  const lead = buildSingleCvLeadText(screenType, objectType);
  const panel = createWidgetPanel({
    position: resolveSingleCvPanelPosition(screenType),
    lead,
    ariaLabel: lead,
    minimizable: true,
    // 効果分析ログ（イベントL、BR-AN-14）: ユーザーの縮小操作を離脱意思として発火する。
    onMinimize: () => trackWidgetMinimize(analyticsCtx),
  });
  shadowRoot.appendChild(panel.root);

  const calendarModal = createCalendarModal();
  shadowRoot.appendChild(calendarModal.element);
  document.body.appendChild(host);

  // I261-77: 未ログイン時の表示（日程調整ベースの固定モザイク＋「ログイン」「新規会員登録」の2ボタン）は renderBookingSection が
  // card 内に描画する。従来の重畳ログインゲート（panel.showLoginGate）は未ログイン表示から廃止した。
  // LOGIN_PROMPT も renderBookingSection 内で描画時に1回発火する（従来と同じタイミング）。

  // モック準拠: proposal-item（左の赤い縦線）+ card枠内にコンテンツ配置
  const proposalItem = createElement("div", { className: "spica-proposal-item" });
  const card = createElement("div", { className: "spica-proposal-card" });
  proposalItem.appendChild(card);
  panel.content.appendChild(proposalItem);

  await renderBookingSection(card, panel, calendarModal, {
    memberGroupId,
    screenType,
    objectType,
    objectRootId,
    meetingModePolicy,
    prFlg,
    companyName,
    contentName,
    companyRootId,
    isMainCv,
  });
}
